import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { SocketService } from './socket.service';
import { Subscription } from 'rxjs';
import { secureRandomIndex } from './utils/random';
import { CommonModule } from '@angular/common';
import { VehicleModuleComponent } from './vehicle-module.component';
import { CouplingModuleComponent } from './coupling-module.component';
import { CommandService, CommandRequest } from './command.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TipoAcessorio } from './accessory-type.enum';

@Component({
  selector: 'app-accessory-network',
  standalone: true,
  imports: [CommonModule, VehicleModuleComponent, CouplingModuleComponent, TranslateModule],
  templateUrl: './accessory-network.component.html',
  styleUrls: ['./accessory-network.component.scss']
})
export class AccessoryNetworkComponent implements OnInit, OnDestroy {
  vehicleData: any = {
    vehicle: {
      accessories: []
    },
    couplings: []
  };
  loadingIds = new Set<any>();
  transitioningIds = new Set<any>();
  errorMessages = new Map<any, string>();
  private readonly pendingCommandTickets = new Map<any, string>();
  private currentVeoid = 0;
  private currentUserId: number | null = null;
  private useMockData = false;
  private socketSub!: Subscription;
  private readonly actuatorCommandType = 'SET_ACCESSORY_NETWORK_ACTUATOR_STATE' as const;
  private readonly maxResendAttemptsAfterCancel = 2;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly socketService: SocketService,
    private readonly commandService: CommandService,
    private readonly translateService: TranslateService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.route.paramMap.subscribe((params: any) => {
      const veioidParam = params.get('veioid');
      const useMockData = this.route.snapshot?.queryParamMap?.get('mock') === 'true';
      console.log('Parâmetro veioid da URL:', veioidParam);
      const parsedVeioid = veioidParam === null || veioidParam === '' ? Number.NaN : Number(veioidParam);
      const veioid = Number.isFinite(parsedVeioid) ? parsedVeioid : secureRandomIndex(1_000_000);
      this.currentVeoid = veioid;
      this.useMockData = useMockData;

      const userIdParam = this.route.snapshot?.queryParamMap?.get('userId');
      const parsedUserId = userIdParam === null || userIdParam === '' ? Number.NaN : Number(userIdParam);
      this.currentUserId = Number.isFinite(parsedUserId) ? parsedUserId : null;
      if (useMockData) {
        console.log('Modo mock habilitado para front via query param.');
      }
      console.log('Conectando ao SSE com veioid:', veioid);

      // Desinscrever da conexão anterior se existir
      if (this.socketSub) {
        this.socketSub.unsubscribe();
      }
      
      this.socketSub = this.socketService.getUpdates(veioid, { useMock: useMockData }).subscribe(data => {
        this.vehicleData = this.normalizeVehicleData(data);
        const payloadVeoid = this.coerceNumber(data?.vehicle?.veioid ?? data?.vehicle?.id);
        if (payloadVeoid !== null) {
          this.currentVeoid = payloadVeoid;
        }
        console.log('Dados recebidos via SSE:', this.vehicleData);
        this.cdr.detectChanges(); // Força a detecção de mudanças
      });
    });
  }
  


  ngOnDestroy() {
    if (this.socketSub) {
      this.socketSub.unsubscribe();
    }
  }

  toggleAccessory(accessory: any) {
    if (!this.canExecuteCommand(accessory)) {
      return;
    }

    const key = accessory.id ?? accessory;

    if (this.loadingIds.has(key)) {
      return;
    }

    this.loadingIds.add(key);
    this.errorMessages.delete(key);
    this.cdr.detectChanges();

    const shouldActivate = !this.isAccessoryActive(accessory);
    const userId = this.resolveUserId();
    const accessoryId = this.coerceNumber(accessory?.id);
    if (!userId) {
      this.errorMessages.set(
        key,
        this.translateOrFallback('errors.missingUserId', 'Informe userId na URL para enviar comando (ex: ?userId=12345).'),
      );
      this.loadingIds.delete(key);
      this.cdr.detectChanges();
      return;
    }

    if (accessoryId === null) {
      this.errorMessages.set(
        key,
        this.translateOrFallback('errors.invalidAccessory', 'Acessório inválido: id ausente no payload.'),
      );
      this.loadingIds.delete(key);
      this.cdr.detectChanges();
      return;
    }

    const token = this.resolveCommandBearerToken();
    const payload = this.buildCommandPayload(accessory, userId, shouldActivate, accessoryId);
    const desiredAction = shouldActivate ? 1 : 0;

    this.executeCommandRequest(accessory, payload, token, desiredAction, this.maxResendAttemptsAfterCancel);
  }

  private executeCommandRequest(
    accessory: any,
    payload: CommandRequest,
    token: string | undefined,
    desiredAction: number,
    resendAttemptsAfterCancel: number,
  ): void {
    const key = accessory.id ?? accessory;
    this.commandService.sendCommand(payload, token).subscribe({
      next: (result) => {
        const statusCode = Number(result?.status ?? 200);

        if (statusCode === 409) {
          this.handleCommandPending(accessory, result, payload, token, desiredAction, resendAttemptsAfterCancel);
          return;
        }

        this.applyCommandResult(accessory, desiredAction);
        this.loadingIds.delete(key);
        this.transitioningIds.delete(key);
        this.pendingCommandTickets.delete(key);
        this.cdr.detectChanges();
      },
      error: (err) => {
        const msg = err?.error?.message ??
          err?.error?.error ??
          err?.message ??
          this.translateOrFallback('errors.commandExecution', 'Erro ao executar o comando. Tente novamente.');
        this.errorMessages.set(key, msg);
        this.loadingIds.delete(key);
        this.transitioningIds.delete(key);
        this.pendingCommandTickets.delete(key);
        this.cdr.detectChanges();
      }
    });
  }

  private handleCommandPending(
    accessory: any,
    result: any,
    payload: CommandRequest,
    token: string | undefined,
    desiredAction: number,
    resendAttemptsAfterCancel: number,
  ): void {
    const key = accessory.id ?? accessory;
    const pendingAction = this.extractPendingAction(result);

    // Se já existe comando pendente com a mesma ação desejada, não cancela.
    if (pendingAction !== null && pendingAction === desiredAction) {
      this.applyCommandResult(accessory, desiredAction);
      this.loadingIds.delete(key);
      this.transitioningIds.delete(key);
      this.pendingCommandTickets.delete(key);
      this.errorMessages.delete(key);
      this.cdr.detectChanges();
      return;
    }

    const commandTicket = this.extractCommandTicket(result);

    if (!commandTicket) {
      this.loadingIds.delete(key);
      this.transitioningIds.delete(key);
      this.pendingCommandTickets.delete(key);
      this.cdr.detectChanges();
      return;
    }

    this.transitioningIds.add(key);
    this.pendingCommandTickets.set(key, commandTicket);
    this.errorMessages.delete(key);
    this.commandService.deleteCommandTicket(commandTicket, token).subscribe({
      next: () => {
        if (resendAttemptsAfterCancel > 0) {
          this.transitioningIds.delete(key);
          this.pendingCommandTickets.delete(key);
          this.executeCommandRequest(accessory, payload, token, desiredAction, resendAttemptsAfterCancel - 1);
          return;
        }

        this.loadingIds.delete(key);
        this.transitioningIds.delete(key);
        this.pendingCommandTickets.delete(key);
        this.cdr.detectChanges();
      },
      error: () => {
        this.loadingIds.delete(key);
        this.transitioningIds.delete(key);
        this.pendingCommandTickets.delete(key);
        this.cdr.detectChanges();
      }
    });
  }

  private extractCommandTicket(result: any): string | null {
    const fromRoot = result?.commandTicket;
    if (typeof fromRoot === 'string' && fromRoot.trim().length > 0) {
      return fromRoot;
    }

    const fromNested = result?.command?.commandTicket;
    if (typeof fromNested === 'string' && fromNested.trim().length > 0) {
      return fromNested;
    }

    return null;
  }

  private extractPendingAction(result: any): number | null {
    const rawAction = result?.command?.deviceCommand?.action ?? result?.data?.command?.deviceCommand?.action;
    if (rawAction === null || rawAction === undefined || rawAction === '') {
      return null;
    }

    if (typeof rawAction === 'boolean') {
      return rawAction ? 1 : 0;
    }

    const parsed = Number(rawAction);
    if (!Number.isFinite(parsed)) {
      return null;
    }

    return parsed === 0 ? 0 : 1;
  }

  private resolveUserId(): number | null {
    if (this.currentUserId && this.currentUserId > 0) {
      return this.currentUserId;
    }

    const storageUserId = Number(localStorage.getItem('userId') ?? Number.NaN);
    if (Number.isFinite(storageUserId) && storageUserId > 0) {
      return storageUserId;
    }

    return null;
  }

  private resolveCommandBearerToken(): string | undefined {
    const pageToken = this.resolvePageToken();
    if (pageToken) {
      return pageToken;
    }

    const sseToken = this.normalizeBearerToken(this.socketService.authToken);
    return sseToken ?? undefined;
  }

  private resolvePageToken(): string | null {
    const queryToken = this.route.snapshot?.queryParamMap?.get('token')
      ?? this.route.snapshot?.queryParamMap?.get('access_token')
      ?? this.route.snapshot?.queryParamMap?.get('bearer');

    const normalizedQueryToken = this.normalizeBearerToken(queryToken);
    if (normalizedQueryToken) {
      return normalizedQueryToken;
    }

    const browserGlobal = globalThis as typeof globalThis & {
      localStorage?: Storage;
      sessionStorage?: Storage;
    };
    if (!browserGlobal.localStorage && !browserGlobal.sessionStorage) {
      return null;
    }

    const storageKeys = ['token', 'access_token', 'authToken', 'bearerToken'];
    for (const key of storageKeys) {
      const storageToken = this.normalizeBearerToken(
        browserGlobal.localStorage?.getItem(key) ?? browserGlobal.sessionStorage?.getItem(key),
      );
      if (storageToken) {
        return storageToken;
      }
    }

    return null;
  }

  private normalizeBearerToken(value: unknown): string | null {
    if (typeof value !== 'string') {
      return null;
    }

    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    return trimmed.replaceAll(/^Bearer\s+/gi, '').trim() || null;
  }

  private buildCommandPayload(accessory: any, userId: number, shouldActivate: boolean, accessoryId: number): CommandRequest {
    const serialNumber = this.resolveSerialNumber(accessory);
    const group = this.resolveGroup(accessory);
    const deviceType = this.coerceNumber(accessory?.type);

    const deviceCommand: CommandRequest['deviceCommand'] = {
      group,
      type: deviceType,
      accessoryId,
      action: shouldActivate ? "1" : "0",
    };

    if (serialNumber !== null) {
      deviceCommand.serialNumber = serialNumber;
    }

    return {
      userId,
      veoid: this.currentVeoid,
      type: this.actuatorCommandType,
      deviceCommand,
    };
  }

  private resolveSerialNumber(accessory: any): number | null {
    return this.coerceNumber(accessory?.serialNumber);
  }

  private resolveGroup(accessory: any): number | null {
    const group = this.coerceNumber(accessory?.group);
    if (group !== null) {
      return group;
    }

    return 1;
  }

  private coerceNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const num = Number(value);
    if (!Number.isFinite(num)) {
      return null;
    }

    return num;
  }

  private applyCommandResult(accessory: any, valor: number): void {
    if (typeof accessory.active === 'boolean') {
      accessory.active = !accessory.active;
      accessory.status = accessory.active ? 'Ativo' : 'Inativo';
    } else if (typeof accessory.status === 'string') {
      accessory.status = this.toggleStatusString(accessory.status);
    } else {
      accessory.active = valor === 1;
      accessory.status = accessory.active ? 'Ativo' : 'Inativo';
    }
    accessory.value = accessory.status;
  }

  private toggleStatusString(status: string): string {
    const n = this.normalizeText(status);
    const isInactive = n.includes('inativo') || n.includes('inactive') || n.includes('desligado');
    if (isInactive) {
      return status.toLowerCase().includes('inativo') ? 'Ativo' : 'Active';
    }
    return status.toLowerCase().includes('ativo') ? 'Inativo' : 'Inactive';
  }

  isLoading(accessory: any): boolean {
    return this.loadingIds.has(accessory.id ?? accessory);
  }

  getErrorMessage(accessory: any): string | null {
    if (this.isTransitioning(accessory)) {
      return null;
    }

    return this.errorMessages.get(accessory.id ?? accessory) ?? null;
  }

  isTransitioning(accessory: any): boolean {
    return this.transitioningIds.has(accessory.id ?? accessory);
  }

  shouldShowActionButton(accessory: any): boolean {
    return this.isTrunkLock(accessory) && this.isActuator(accessory) && this.isCompatible(accessory);
  }

  getActionLabel(accessory: any): string {
    return this.isAccessoryActive(accessory)
      ? this.translateOrFallback('accessory.action.deactivate', 'Desativar')
      : this.translateOrFallback('accessory.action.activate', 'Ativar');
  }

  getMainVehicleLabel(): string {
    return this.translateOrFallback('labels.mainVehicle', 'Veículo Principal');
  }

  getTrailerIdentifierLabel(): string {
    return this.translateOrFallback('labels.trailerIdentifier', 'Identificador de Carreta');
  }

  formatAccessoryValue(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    const normalizedValue = String(value).replaceAll(',', '.').trim();
    const numValue = Number(normalizedValue);
    if (!Number.isFinite(numValue)) {
      return String(value);
    }

    const locale = this.getNumberLocale();

    // Detecta casas decimais automaticamente (máx 2)
    const decimalPlaces = normalizedValue.includes('.')
      ? Math.min(2, normalizedValue.split('.')[1]?.length ?? 0)
      : 0;

    try {
      return new Intl.NumberFormat(locale, {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces
      }).format(numValue);
    } catch {
      // Fallback caso locale não seja suportado
      return String(value);
    }
  }

  private getNumberLocale(): string {
    const translatedLocale = this.translateService.instant('locale');
    if (translatedLocale && translatedLocale !== 'locale') {
      return translatedLocale;
    }

    return 'pt-BR';
  }

  private canExecuteCommand(accessory: any): boolean {
    return this.isActuator(accessory) && this.isTrunkLock(accessory);
  }

  private normalizeVehicleData(data: any): any {
    const safeData = data || { vehicle: { accessories: [] }, couplings: [] };
    const couplings = Array.isArray(safeData.couplings) ? safeData.couplings : [];

    return {
      ...safeData,
      vehicle: {
        accessories: Array.isArray(safeData.vehicle?.accessories) ? safeData.vehicle.accessories : []
      },
      couplings: couplings.filter((coupling: any) => !this.shouldRemoveCoupling(coupling))
    };
  }

  private shouldRemoveCoupling(coupling: any): boolean {
    const identifier = coupling?.plate ?? coupling?.placa ?? coupling?.identifier ?? coupling?.identificador;
    const hasMissingIdentifier = identifier === null || identifier === undefined || identifier === '';
    if (hasMissingIdentifier) {
      return true;
    }

    const status = this.normalizeText(
      coupling?.identifierStatus ??
      coupling?.identificadorStatus ??
      coupling?.connectionStatus ??
      coupling?.status
    );

    return status.includes('desconectado') ||
      status.includes('desinstalado') ||
      status.includes('disconnected') ||
      status.includes('uninstalled');
  }

  private isCompatible(accessory: any): boolean {
    const compatibilityFlags = [
      accessory?.compatible,
      accessory?.isCompatible,
      accessory?.available,
      accessory?.disponivel,
      accessory?.enabled
    ].filter(value => typeof value === 'boolean');

    if (compatibilityFlags.length === 0) {
      return true;
    }

    return compatibilityFlags.some(Boolean);
  }

  private isActuator(accessory: any): boolean {
    if (typeof accessory?.isActuator === 'boolean') {
      return accessory.isActuator;
    }

    // Suporte ao tipo numérico (RNF006): 2 = TIPO_ACESSORIO_SAIDA_DIGITAL
    if (typeof accessory?.type === 'number' || typeof accessory?.tipoAcessorio === 'number') {
      const numType = accessory?.tipoAcessorio ?? accessory?.type;
      return numType === TipoAcessorio.TIPO_ACESSORIO_SAIDA_DIGITAL;
    }

    const type = this.normalizeText(accessory?.type ?? accessory?.tipoAcessorio ?? accessory?.category ?? accessory?.kind);
    // Reconhece 'atuador', 'actuator', 'saida_digital' e o nome completo do enum
    return type.includes('atuador') ||
      type.includes('actuator') ||
      type.includes('saida_digital') ||
      type.includes('saida digital') ||
      type === 'tipo_acessorio_saida_digital';
  }

  private isTrunkLock(accessory: any): boolean {
    const accessoryName = this.normalizeText(accessory?.name ?? accessory?.description ?? accessory?.label);
    const accessoryCode = this.normalizeText(accessory?.code ?? accessory?.id);
    return accessoryName.includes('trava de bau') ||
      accessoryName.includes('trava bau') ||
      accessoryName.includes('bau lock') ||
      accessoryCode.includes('trava_bau') ||
      accessoryCode.includes('bau_lock');
  }

  isAccessoryActive(accessory: any): boolean {
    if (typeof accessory?.active === 'boolean') {
      return accessory.active;
    }

    const status = this.normalizeText(accessory?.status);
    return (status.includes('ativo') && !status.includes('inativo')) ||
      status.includes('active') ||
      (status.includes('ligado') && !status.includes('desligado')) ||
      status.includes('locked') ||
      status.includes('fechado');
  }

  private normalizeText(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }

    if (typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'boolean') {
      return '';
    }

    return `${value}`
      .normalize('NFD')
      .replaceAll(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }

  private translateOrFallback(key: string, fallback: string): string {
    const translated = this.translateService.instant(key);
    return translated && translated !== key ? translated : fallback;
  }
}